import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const VIEWPORT_AUTOPLAY_LOOKAHEAD = '300px 0px';
const VIEWPORT_AUTOPLAY_VISIBLE_THRESHOLD = 0.6;

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {IntersectionObserver | null} */
  #viewportLookaheadObserver = null;

  /** @type {IntersectionObserver | null} */
  #viewportVisibilityObserver = null;

  #viewportVisible = false;

  #programmaticViewportPause = false;

  #viewportSetupPending = false;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  #handleReducedMotionChange = () => {
    if (!this.hasAttribute('data-viewport-autoplay')) return;

    if (this.#reducedMotion.matches) {
      this.pauseMedia();
      return;
    }

    this.#setupViewportAutoplay();
  };

  get viewportAutoplayThreshold() {
    const configured = Number(this.getAttribute('data-viewport-autoplay-threshold'));
    return configured > 0 && configured <= 1 ? configured : VIEWPORT_AUTOPLAY_VISIBLE_THRESHOLD;
  }

  #handleViewportVisibility = (entries) => {
    const entry = entries[0];
    this.#viewportVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= this.viewportAutoplayThreshold);

    if (!this.#viewportVisible || document.hidden) {
      this.pauseMedia();
      return;
    }

    if (this.#reducedMotion.matches || this.hasAttribute('data-user-paused')) return;
    void this.playMedia();
  };

  #handleDocumentVisibility = () => {
    if (!this.hasAttribute('data-viewport-autoplay')) return;

    if (document.hidden || !this.#viewportVisible) {
      this.pauseMedia();
      return;
    }

    if (this.#reducedMotion.matches || this.hasAttribute('data-user-paused')) return;
    void this.playMedia();
  };

  #setupViewportAutoplay() {
    if (!this.hasAttribute('data-viewport-autoplay') || this.#reducedMotion.matches) return;
    if (!('IntersectionObserver' in window)) return;

    if (this.#viewportVisibilityObserver) {
      this.#handleDocumentVisibility();
      return;
    }

    if (this.hasAttribute('data-defer-until-load') && document.readyState !== 'complete') {
      if (this.#viewportSetupPending) return;
      this.#viewportSetupPending = true;
      window.addEventListener('load', () => this.#setupViewportAutoplay(), {
        once: true,
        signal: this.#abortController.signal,
      });
      return;
    }

    this.#viewportLookaheadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || this.#reducedMotion.matches) return;
        this.loadContent(false, false);
        this.#viewportLookaheadObserver?.disconnect();
        this.#viewportLookaheadObserver = null;
      },
      { rootMargin: VIEWPORT_AUTOPLAY_LOOKAHEAD, threshold: 0 }
    );
    this.#viewportLookaheadObserver.observe(this);

    this.#viewportVisibilityObserver = new IntersectionObserver(this.#handleViewportVisibility, {
      threshold: [0, this.viewportAutoplayThreshold],
    });
    this.#viewportVisibilityObserver.observe(this);
    this.#viewportSetupPending = false;
  }

  #handleMediaStartedPlaying = (/** @type {MediaStartedPlayingEvent} */ event) => {
    if (event.detail?.resource === this) return;
    this.pauseMedia();
  };

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    super.connectedCallback();
    const signal = this.#abortController.signal;
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    document.addEventListener('visibilitychange', this.#handleDocumentVisibility, { signal });
    this.#reducedMotion.addEventListener('change', this.#handleReducedMotionChange, { signal });
    this.#setupViewportAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#viewportLookaheadObserver?.disconnect();
    this.#viewportVisibilityObserver?.disconnect();
    this.#viewportLookaheadObserver = null;
    this.#viewportVisibilityObserver = null;
    this.#viewportSetupPending = false;
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      toggleMediaButton.dataset.state = isPlaying ? 'playing' : 'paused';
      toggleMediaButton.setAttribute('aria-label', isPlaying ? toggleMediaButton.dataset.pauseLabel || '' : toggleMediaButton.dataset.playLabel || '');
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.removeAttribute('data-user-paused');
    this.loadContent(true);
    void this.playMedia();
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   * @param {boolean} [announce] - Whether to announce that media is starting
   * @returns {Element | undefined}
   */
  loadContent(focus = true, announce = true) {
    if (this.getAttribute('data-media-loaded')) {
      return this.querySelector('video, iframe, model-viewer') ?? undefined;
    }

    if (announce) this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (
      !(content instanceof HTMLVideoElement) ||
      (!this.hasAttribute('data-product-video') && !this.hasAttribute('data-viewport-autoplay'))
    ) {
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    }

    if (content instanceof HTMLVideoElement) {
      content.addEventListener('playing', () => {
        this.setAttribute('data-video-ready', '');
        this.removeAttribute('data-video-error');
        this.removeAttribute('data-user-paused');
        this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
        this.isPlaying = true;
        this.updatePlayPauseHint(true);
      });
      content.addEventListener('pause', () => {
        if (this.hasAttribute('data-viewport-autoplay') && !this.#programmaticViewportPause && this.#viewportVisible) {
          this.setAttribute('data-user-paused', '');
        }
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
      });
      content.addEventListener('ended', () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
      });
      content.addEventListener('error', () => {
        this.removeAttribute('data-video-ready');
        this.setAttribute('data-video-error', '');
        this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
      });
    }

    return content;
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      if (this.hasAttribute('data-product-video') || this.hasAttribute('data-viewport-autoplay')) {
        this.setAttribute('data-user-paused', '');
      }
      this.pauseMedia();
    } else {
      this.removeAttribute('data-user-paused');
      void this.playMedia();
    }
  }

  async playMedia() {
    const wasLoaded = this.hasAttribute('data-media-loaded');
    if (!wasLoaded) this.loadContent(false);
    if (
      wasLoaded &&
      (this.hasAttribute('data-product-video') || this.hasAttribute('data-viewport-autoplay'))
    ) {
      this.dispatchEvent(new MediaStartedPlayingEvent(this));
    }

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      return true;
    } else {
      const video = this.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) {
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
        return false;
      }

      if (this.hasAttribute('data-product-video')) {
        video.muted = true;
        video.defaultMuted = true;
      }

      try {
        await video.play();
      } catch {
        if ((this.hasAttribute('data-product-video') || this.hasAttribute('data-viewport-autoplay')) && !this.hasAttribute('data-video-ready')) {
          this.setAttribute('data-video-error', '');
          this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
        }
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
        return false;
      }

      this.isPlaying = !video.paused;
      this.updatePlayPauseHint(this.isPlaying);
      return this.isPlaying;
    }
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        if (this.hasAttribute('data-viewport-autoplay')) this.#programmaticViewportPause = true;
        video.pause();
        if (this.hasAttribute('data-viewport-autoplay')) {
          queueMicrotask(() => {
            this.#programmaticViewportPause = false;
          });
        }
      }
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    super.connectedCallback();

    if (this.modelViewerUI) {
      const element = this.querySelector('model-viewer');
      if (element) this.#bindModelViewerInteractionListeners(element);
    }
  }

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  async playMedia() {
    await super.playMedia();
    this.modelViewerUI?.play();
    this.isPlaying = Boolean(this.modelViewerUI);
    this.updatePlayPauseHint(this.isPlaying);
    return this.isPlaying;
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    this.#bindModelViewerInteractionListeners(element);
  }

  #bindModelViewerInteractionListeners(element) {
    const { signal } = this.#abortController;

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
